import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, X, Check, CheckCheck, Trophy, Target, Award, 
  AlertTriangle, AlertOctagon, TrendingDown, CheckCircle, 
  XCircle, ShieldOff, Star, DollarSign, Trash2
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';

const ICON_MAP = {
  'trophy': Trophy,
  'target': Target,
  'award': Award,
  'alert-triangle': AlertTriangle,
  'alert-octagon': AlertOctagon,
  'trending-down': TrendingDown,
  'check-circle': CheckCircle,
  'x-circle': XCircle,
  'shield-off': ShieldOff,
  'star': Star,
  'dollar-sign': DollarSign,
  'bell': Bell
};

const COLOR_MAP = {
  'green': 'text-green-400 bg-green-500/20',
  'cyan': 'text-cyan-400 bg-cyan-500/20',
  'gold': 'text-yellow-400 bg-yellow-500/20',
  'yellow': 'text-yellow-400 bg-yellow-500/20',
  'orange': 'text-orange-400 bg-orange-500/20',
  'red': 'text-red-400 bg-red-500/20',
  'blue': 'text-blue-400 bg-blue-500/20'
};

const NotificationItem = ({ notification, onRead, onDelete }) => {
  const IconComponent = ICON_MAP[notification.icon] || Bell;
  const colorClass = COLOR_MAP[notification.color] || COLOR_MAP.blue;
  
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className={`p-4 border-b border-white/5 hover:bg-white/5 transition-colors ${
        !notification.read ? 'bg-blue-500/5' : ''
      }`}
    >
      <div className="flex gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorClass}`}>
          <IconComponent className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className={`font-medium ${!notification.read ? 'text-white' : 'text-gray-400'}`}>
                {notification.title}
              </p>
              <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                {notification.message}
              </p>
            </div>
            {notification.priority === 'critical' && (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs shrink-0">
                URGENT
              </Badge>
            )}
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-600">{formatTime(notification.created_at)}</span>
            <div className="flex gap-2">
              {!notification.read && (
                <button
                  onClick={() => onRead(notification.id)}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Mark read
                </button>
              )}
              <button
                onClick={() => onDelete(notification.id)}
                className="text-xs text-gray-500 hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get('/notifications?limit=20'),
        api.get('/notifications/count')
      ]);
      setNotifications(notifRes.data.notifications || []);
      setUnreadCount(countRes.data.unread || 0);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  // Fetch on mount and periodically
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(notifications.filter(n => n.id !== notificationId));
      if (!notification?.read) {
        setUnreadCount(Math.max(0, unreadCount - 1));
      }
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-lg hover:bg-white/5 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-gray-400 hover:text-white transition-colors" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] bg-[hsl(220,20%,7%)] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
          >
            {/* Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-cyan-400" />
                <h3 className="font-heading font-bold text-white">Notifications</h3>
                {unreadCount > 0 && (
                  <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                    {unreadCount} new
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1"
                  >
                    <CheckCheck className="w-3 h-3" />
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-white/5 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-[calc(70vh-60px)] overflow-y-auto">
              {notifications.length > 0 ? (
                <AnimatePresence>
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onRead={handleMarkRead}
                      onDelete={handleDelete}
                    />
                  ))}
                </AnimatePresence>
              ) : (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-white/5 flex items-center justify-center mb-4">
                    <Bell className="w-8 h-8 text-gray-600" />
                  </div>
                  <p className="text-gray-500">No notifications yet</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Trading alerts will appear here
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
